import { Client, Room } from 'colyseus.js';

export interface Player {
  x: number;
  y: number;
  velocityX: number;
  velocityY: number;
  facing: string;
  onGround: boolean;
  sessionId: string;
  letter: string;
}

export interface Platform {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface TargetZone {
  x: number;
  y: number;
  width: number;
  height: number;
  index: number;
}

export interface GameState {
  players: Map<string, Player>;
  platforms: Platform[];
  targetZones: TargetZone[];
  currentWord: string;
  gameStarted: boolean;
  level: number;
  gameWon: boolean;
  timeRemaining: number;
  gameOver: boolean;
  readyPlayers: number;
}

export class ColyseusClient {
  private client: Client;
  private room: Room<GameState> | null = null;
  private onStateChangeCallback?: (state: GameState) => void;
  private onPlayerJoinCallback?: (player: Player) => void;
  private onPlayerLeaveCallback?: (sessionId: string) => void;

  constructor(serverUrl?: string) {
    // Auto-detect server URL when served from same origin, or use environment variable, or default to localhost
    let url: string;
    if (serverUrl) {
      url = serverUrl;
    } else if (import.meta.env.VITE_COLYSEUS_SERVER_URL) {
      url = import.meta.env.VITE_COLYSEUS_SERVER_URL;
    } else if (typeof window !== 'undefined') {
      // Auto-detect: if served from same origin, use current protocol/host
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const host = window.location.host;
      url = `${protocol}//${host}`;
    } else {
      url = 'ws://localhost:2567';
    }
    this.client = new Client(url);
  }

  async connect(roomName: string = 'platformer'): Promise<void> {
    try {
      this.room = await this.client.joinOrCreate<GameState>(roomName);
      console.log('Connected to room:', this.room.roomId);

      // Listen for state changes
      this.room.onStateChange((state) => {
        if (this.onStateChangeCallback) {
          this.onStateChangeCallback(state);
        }
      });

      // Listen for player join/leave
      this.room.onMessage('player-joined', (message) => {
        if (this.onPlayerJoinCallback) {
          this.onPlayerJoinCallback(message);
        }
      });

      this.room.onMessage('player-left', (message) => {
        if (this.onPlayerLeaveCallback) {
          this.onPlayerLeaveCallback(message.sessionId);
        }
      });

      // Listen for errors
      this.room.onError((code, message) => {
        console.error('Room error:', code, message);
      });

      // Listen for leave
      this.room.onLeave((code) => {
        console.log('Left room:', code);
      });
    } catch (error) {
      console.error('Failed to connect:', error);
      throw error;
    }
  }

  getSessionId(): string | null {
    return this.room?.sessionId || null;
  }

  getRoom(): Room<GameState> | null {
    return this.room;
  }

  sendInput(type: 'move-left' | 'move-right' | 'jump' | 'stop'): void {
    if (this.room) {
      this.room.send(type);
    }
  }

  sendToggleReady(): void {
    if (this.room) {
      this.room.send('toggle-ready');
    }
  }

  onStateChange(callback: (state: GameState) => void): void {
    this.onStateChangeCallback = callback;
  }

  onPlayerJoin(callback: (player: Player) => void): void {
    this.onPlayerJoinCallback = callback;
  }

  onPlayerLeave(callback: (sessionId: string) => void): void {
    this.onPlayerLeaveCallback = callback;
  }

  disconnect(): void {
    if (this.room) {
      this.room.leave();
      this.room = null;
    }
  }
}

